import { darkPalette as darkPaletteTokens, lightPalette as lightPaletteTokens } from '@mds/tokens';
import { alpha, createTheme, darken, getContrastRatio, type SimplePaletteColorOptions } from '@mui/material/styles';
import {
  black,
  blue,
  darkBlue,
  darkBlueChannel,
  green,
  grey,
  orange,
  purple,
  red,
  white,
  type ColorRange,
} from './colors';
import type {} from './types';

// Augment MUI's Palette interface with custom properties
declare module '@mui/material/styles' {
  interface TypeText {
    error: string;
    contrast: string;
  }

  interface Palette {
    neutral: {
      main: string;
      contrastText: string;
    };
    assets: {
      neonLogoText: string;
    };
    border: {
      error: string;
      info: string;
      primary: string;
      secondary: string;
      subtle: string;
      strong: string;
      focused: string;
      success: string;
      warning: string;
    };
    link: {
      default: string;
      visited: string;
      pressed: string;
      focus: string;
    };
    scrollbar: {
      thumb: string;
      track: string;
    };
  }

  interface PaletteOptions {
    neutral?: {
      main?: string;
      contrastText?: string;
    };
    assets?: {
      neonLogoText?: string;
    };
    border?: {
      error?: string;
      info?: string;
      primary?: string;
      secondary?: string;
      subtle?: string;
      strong?: string;
      focused?: string;
      success?: string;
      warning?: string;
    };
    link?: {
      default?: string;
      visited?: string;
      pressed?: string;
      focus?: string;
    };
    scrollbar?: {
      thumb?: string;
      track?: string;
    };
  }
}

// WCAG AA https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
// Min contrast ratio is 3 for text larger than 1.5em and 4.5 otherwise
const contrastThreshold = 4.5;

type ColorShade = keyof ColorRange;
type PaletteColorTokens = Partial<SimplePaletteColorOptions>;
type SemanticColorTokenKey = 'subtle' | 'main' | 'dark' | 'text' | 'softBg' | 'softText' | 'contrast';
type SemanticColorTokens = Record<SemanticColorTokenKey, string>;

const primaryTextLight = darkBlue[80];
const primaryTextDark = darkBlue[10];
const lightTextPrimary = lightPaletteTokens.text.primary;
const darkTextPrimary = darkPaletteTokens.text.primary;

const mapSemanticColorTokens = (tokens: SemanticColorTokens, border: string): PaletteColorTokens => ({
  subtle: tokens.subtle,
  main: tokens.main,
  strong: tokens.dark,
  text: tokens.text,
  border,
  softBg: tokens.softBg,
  softText: tokens.softText,
  contrastText: tokens.contrast,
  light: tokens.subtle,
  dark: tokens.dark,
});

const augmentColor = (
  color: ColorRange,
  mode: 'light' | 'dark',
  shades: {
    light?: ColorShade;
    main?: ColorShade;
    dark?: ColorShade;
    text?: ColorShade;
    border?: ColorShade;
    softBg?: ColorShade;
    softText?: ColorShade;
  } = {},
  tokens: PaletteColorTokens = {},
): SimplePaletteColorOptions => {
  const isDark = mode === 'dark';
  const {
    light = isDark ? 90 : 10,
    main = 50,
    dark = isDark ? 10 : 90,
    text = isDark ? 40 : 50,
    border = isDark ? 40 : 50,
    softBg = isDark ? 80 : 20,
    softText = isDark ? 20 : 80,
  } = shades;
  return {
    subtle: tokens.subtle ?? color[light],
    main: tokens.main ?? color[main],
    strong: tokens.strong ?? color[dark],
    text: tokens.text ?? color[text],
    border: tokens.border ?? color[border],
    softBg: tokens.softBg ?? color[softBg],
    softText: tokens.softText ?? color[softText],
    solidColor: tokens.solidColor ?? color[10],
    solidBg: tokens.solidBg ?? color[main],
    solidHoverBg: tokens.solidHoverBg ?? darken(color[main], 0.24),
    solidPressedBg: tokens.solidPressedBg ?? darken(color[main], 0.4),
    contrastText:
      tokens.contrastText ??
      (getContrastRatio(color[main], primaryTextLight) >= contrastThreshold ? primaryTextLight : primaryTextDark),
    // MUI internal
    light: tokens.light ?? color[light],
    // MUI internal
    dark: tokens.dark ?? color[dark],
  };
};

/**
 * TODO Work in progress - action tokens need to be refined documented
 */
export const lightPalette = createTheme({
  palette: {
    action: {
      disabledOpacity: 0.38,
      activeChannel: darkBlueChannel[90],
      hoverOpacity: 0.06,
      pressedOpacity: 0.1,
      selectedOpacity: 0.14,
      active: primaryTextLight,
      disabled: alpha(black, 0.32),
      focus: alpha(darkBlue[90], 0.12),
      hover: lightPaletteTokens.action.hover,
      pressed: lightPaletteTokens.action.pressed,
      selected: lightPaletteTokens.action.selected,
      enabled: lightPaletteTokens.action.enabled,
      disabledBackground: lightPaletteTokens.action.disabledBackground,
      focused: lightPaletteTokens.action.focused,
      focusOutline: lightPaletteTokens.action.focusOutline,
      selectedContrast: lightPaletteTokens.action.selectedContrast,
    },
    background: {
      paper: lightPaletteTokens.background.level1,
      default: lightPaletteTokens.background.default,
      level1: lightPaletteTokens.background.level1,
      level2: lightPaletteTokens.background.level2,
    },
    text: {
      primary: lightPaletteTokens.text.primary,
      secondary: lightPaletteTokens.text.secondary,
      disabled: lightPaletteTokens.text.disabled,
      contrast: lightPaletteTokens.text.contrast,
      error: lightPaletteTokens.error.text,
    },
    link: {
      default: lightPaletteTokens.link.default,
      visited: lightPaletteTokens.link.visited,
      pressed: lightPaletteTokens.link.pressed,
    },
    border: {
      error: lightPaletteTokens.border.error,
      info: lightPaletteTokens.border.info,
      primary: lightPaletteTokens.border.primary,
      secondary: lightPaletteTokens.border.secondary,
      subtle: lightPaletteTokens.border.subtle,
      strong: lightPaletteTokens.border.strong,
      focused: lightPaletteTokens.action.focusOutline,
      success: lightPaletteTokens.border.success,
      warning: lightPaletteTokens.border.warning,
    },
    divider: lightPaletteTokens.border.subtle,
    scrollbar: {
      thumb: lightPaletteTokens.scrollbar.thumb,
      track: lightPaletteTokens.scrollbar.track,
    },
    primary: augmentColor(
      purple,
      'light',
      {},
      mapSemanticColorTokens(lightPaletteTokens.primary, lightPaletteTokens.border.primary),
    ),
    secondary: augmentColor(
      darkBlue,
      'light',
      {},
      mapSemanticColorTokens(lightPaletteTokens.secondary, lightPaletteTokens.border.secondary),
    ),
    error: augmentColor(
      red,
      'light',
      {},
      mapSemanticColorTokens(lightPaletteTokens.error, lightPaletteTokens.border.error),
    ),
    info: augmentColor(
      blue,
      'light',
      {},
      mapSemanticColorTokens(lightPaletteTokens.info, lightPaletteTokens.border.info),
    ),
    success: augmentColor(
      green,
      'light',
      {},
      mapSemanticColorTokens(lightPaletteTokens.success, lightPaletteTokens.border.success),
    ),
    warning: augmentColor(
      orange,
      'light',
      {},
      mapSemanticColorTokens(lightPaletteTokens.warning, lightPaletteTokens.border.warning),
    ),
    neutral: {
      main: grey[30],
      contrastText: grey[60],
    },
    assets: {
      neonLogoText: primaryTextLight,
    },
    contrastThreshold,
    /**
     * Infer light or dark primary text colour based on the given background colour
     */
    getContrastText: (background: string) =>
      getContrastRatio(background, lightTextPrimary) >= contrastThreshold ? primaryTextLight : primaryTextDark,
  },
}).palette;

export const darkPalette = createTheme({
  palette: {
    action: {
      disabledOpacity: 0.38,
      activeChannel: darkBlueChannel[10],
      hoverOpacity: 0.06,
      pressedOpacity: 0.1,
      selectedOpacity: 0.14,
      active: primaryTextDark,
      disabled: alpha(white, 0.32),
      focus: alpha(darkBlue[10], 0.12),
      hover: darkPaletteTokens.action.hover,
      pressed: darkPaletteTokens.action.pressed,
      selected: darkPaletteTokens.action.selected,
      enabled: darkPaletteTokens.action.enabled,
      disabledBackground: darkPaletteTokens.action.disabledBackground,
      focused: darkPaletteTokens.action.focused,
      focusOutline: darkPaletteTokens.action.focusOutline,
      selectedContrast: darkPaletteTokens.action.selectedContrast,
    },
    background: {
      paper: darkPaletteTokens.background.level1,
      default: darkPaletteTokens.background.default,
      level1: darkPaletteTokens.background.level1,
      level2: darkPaletteTokens.background.level2,
    },
    text: {
      primary: darkPaletteTokens.text.primary,
      secondary: darkPaletteTokens.text.secondary,
      disabled: darkPaletteTokens.text.disabled,
      contrast: darkPaletteTokens.text.contrast,
      error: darkPaletteTokens.error.text,
    },
    link: {
      default: darkPaletteTokens.link.default,
      visited: darkPaletteTokens.link.visited,
      pressed: darkPaletteTokens.link.pressed,
    },
    border: {
      error: darkPaletteTokens.border.error,
      info: darkPaletteTokens.border.info,
      primary: darkPaletteTokens.border.primary,
      secondary: darkPaletteTokens.border.secondary,
      subtle: darkPaletteTokens.border.subtle,
      strong: darkPaletteTokens.border.strong,
      focused: darkPaletteTokens.action.focusOutline,
      success: darkPaletteTokens.border.success,
      warning: darkPaletteTokens.border.warning,
    },
    divider: darkPaletteTokens.border.subtle,
    scrollbar: {
      thumb: darkPaletteTokens.scrollbar.thumb,
      track: darkPaletteTokens.scrollbar.track,
    },
    primary: augmentColor(
      purple,
      'dark',
      { text: 30 },
      mapSemanticColorTokens(darkPaletteTokens.primary, darkPaletteTokens.border.primary),
    ),
    secondary: augmentColor(
      darkBlue,
      'dark',
      { text: 30 },
      mapSemanticColorTokens(darkPaletteTokens.secondary, darkPaletteTokens.border.secondary),
    ),
    error: augmentColor(
      red,
      'dark',
      {},
      mapSemanticColorTokens(darkPaletteTokens.error, darkPaletteTokens.border.error),
    ),
    info: augmentColor(blue, 'dark', {}, mapSemanticColorTokens(darkPaletteTokens.info, darkPaletteTokens.border.info)),
    success: augmentColor(
      green,
      'dark',
      {},
      mapSemanticColorTokens(darkPaletteTokens.success, darkPaletteTokens.border.success),
    ),
    warning: augmentColor(
      orange,
      'dark',
      {},
      mapSemanticColorTokens(darkPaletteTokens.warning, darkPaletteTokens.border.warning),
    ),
    neutral: {
      main: darkBlue[60],
      contrastText: darkBlue[30],
    },
    assets: {
      neonLogoText: darkBlue[30],
    },
    contrastThreshold,
    /**
     * Infer light or dark primary text colour based on the given background colour
     */
    getContrastText: (background: string) =>
      getContrastRatio(background, darkTextPrimary) >= contrastThreshold ? primaryTextDark : primaryTextLight,
  },
}).palette;
